import { useState, useRef, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100;
const DELETE_CONCURRENCY = 3;
const RESOLVE_BATCH_SIZE = 20;
const RESOLVE_CONCURRENCY = 3;
const FAILED_STATUSES = ["FAILED", "CANCELLED", "CANCELED", "TIMEOUT", "ERROR"];

function toIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function previousMonthRange() {
  const now = new Date();
  return [
    toIsoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    toIsoDate(new Date(now.getFullYear(), now.getMonth(), 0)),
  ];
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function callApi(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
  return data;
}

const STATUS_BADGES = {
  deleted: { className: "ar-state-badge ar-state-completed", label: "DELETED" },
  not_found: { className: "ar-state-badge ar-state-pending", label: "NOT FOUND" },
  failed: { className: "ar-badge ar-badge-revoke", label: "FAILED" },
};

const DONE_STATUSES = ["deleted", "not_found"];

const LOOKUP_BADGES = {
  pending: { className: "ar-state-badge ar-state-cancelled", label: "LOOKING UP…" },
  not_found: { className: "ar-state-badge ar-state-cancelled", label: "NO IFS USER" },
  multiple: { className: "ar-badge ar-badge-revoke", label: "MULTIPLE ACCOUNTS" },
  error: { className: "ar-badge ar-badge-revoke", label: "LOOKUP FAILED" },
};

function csvCell(value) {
  let s = String(value ?? "");
  // Prevent spreadsheet formula injection
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const ErrorIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export default function InforUserPurge() {
  const [defaultStart, defaultEnd] = previousMonthRange();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [phase, setPhase] = useState("idle"); // idle | querying | resolving | ready | deleting | done
  const [queryStatus, setQueryStatus] = useState("");
  const [queryResult, setQueryResult] = useState(null);
  const [lookups, setLookups] = useState({});
  const [resolveProgress, setResolveProgress] = useState({ done: 0, total: 0 });
  const [selected, setSelected] = useState(new Set());
  const [deleteResults, setDeleteResults] = useState({});
  const [deleteProgress, setDeleteProgress] = useState({ done: 0, total: 0 });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAck, setConfirmAck] = useState(false);
  const [error, setError] = useState("");
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const busy = phase === "querying" || phase === "resolving" || phase === "deleting";
  // Rows without a Data Fabric GUID take theirs from the IFS lookup by employee number
  const rows = (queryResult?.rows || []).map((r) => {
    if (r.guid) return { ...r, key: r.guid, source: "datafabric" };
    const lookup = lookups[r.employeeNumber];
    return {
      ...r,
      key: `emp:${r.employeeNumber}`,
      source: "ifs",
      guid: lookup?.status === "found" ? lookup.guid : "",
      lookup: lookup || { status: "pending", message: "" },
    };
  });

  const resolveMissing = async (data) => {
    const emps = data.rows.filter((r) => !r.guid).map((r) => r.employeeNumber);
    if (emps.length === 0) return;

    setPhase("resolving");
    setResolveProgress({ done: 0, total: emps.length });
    const batches = [];
    for (let i = 0; i < emps.length; i += RESOLVE_BATCH_SIZE) batches.push(emps.slice(i, i + RESOLVE_BATCH_SIZE));

    let done = 0;
    const worker = async () => {
      while (batches.length > 0 && mountedRef.current) {
        const batch = batches.shift();
        let results;
        try {
          ({ results } = await callApi("/myhr/purge/resolve", {
            method: "POST",
            body: JSON.stringify({ queryId: data.queryId, employeeNumbers: batch }),
          }));
        } catch (err) {
          results = batch.map((e) => ({ employeeNumber: e, status: "error", guid: "", message: err.message }));
        }
        done += batch.length;
        if (!mountedRef.current) return;
        setLookups((prev) => ({ ...prev, ...Object.fromEntries(results.map((r) => [r.employeeNumber, r])) }));
        if (!data.truncated) {
          setSelected((prev) => {
            const next = new Set(prev);
            results.forEach((r) => { if (r.status === "found") next.add(r.guid); });
            return next;
          });
        }
        setResolveProgress({ done, total: emps.length });
      }
    };

    await Promise.all(Array.from({ length: RESOLVE_CONCURRENCY }, worker));
    if (mountedRef.current) setPhase("ready");
  };

  const runQuery = async () => {
    if (!startDate || !endDate) {
      setError("Select both a start and end date.");
      return;
    }
    if (startDate > endDate) {
      setError("Start date must be on or before end date.");
      return;
    }

    setPhase("querying");
    setError("");
    setQueryResult(null);
    setLookups({});
    setDeleteResults({});
    setSelected(new Set());
    setConfirmOpen(false);
    setConfirmAck(false);
    setQueryStatus("SUBMITTING");

    try {
      const { queryId } = await callApi("/myhr/purge/query", {
        method: "POST",
        body: JSON.stringify({ startDate, endDate }),
      });

      for (let i = 0; i < MAX_POLLS && mountedRef.current; i++) {
        const data = await callApi(`/myhr/purge/query?queryId=${encodeURIComponent(queryId)}`);
        setQueryStatus(data.status);

        if (data.status === "FINISHED") {
          setQueryResult(data);
          setSelected(data.truncated ? new Set() : new Set(data.rows.filter((r) => r.guid).map((r) => r.guid)));
          setPhase("ready");
          await resolveMissing(data);
          return;
        }
        if (FAILED_STATUSES.includes(data.status)) {
          throw new Error(`Data Fabric query ended with status ${data.status}`);
        }
        await sleep(POLL_INTERVAL_MS);
      }
      throw new Error("Timed out waiting for the Data Fabric query to finish.");
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
        setPhase("idle");
      }
    }
  };

  const toggleRow = (guid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  };

  const isDone = (guid) => DONE_STATUSES.includes(deleteResults[guid]?.status);
  const selectableRows = rows.filter((r) => r.guid && !isDone(r.guid));
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.guid));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableRows.map((r) => r.guid)));
  };

  const handleReset = () => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setPhase("idle");
    setQueryStatus("");
    setQueryResult(null);
    setLookups({});
    setSelected(new Set());
    setDeleteResults({});
    setConfirmOpen(false);
    setConfirmAck(false);
    setError("");
  };

  const runDelete = async () => {
    const guids = selectableRows.filter((r) => selected.has(r.guid)).map((r) => r.guid);
    if (guids.length === 0) return;

    setConfirmOpen(false);
    setConfirmAck(false);
    setPhase("deleting");
    setError("");
    setDeleteProgress({ done: 0, total: guids.length });

    const collected = {};
    const queue = [...guids];
    const empByGuid = Object.fromEntries(rows.filter((r) => r.source === "ifs" && r.guid).map((r) => [r.guid, r.employeeNumber]));
    // One user per request so a slow IFS delete can't time out a whole batch
    const worker = async () => {
      while (queue.length > 0 && mountedRef.current) {
        const guid = queue.shift();
        try {
          const data = await callApi("/myhr/purge/delete", {
            method: "POST",
            body: JSON.stringify({
              queryId: queryResult.queryId,
              guids: [guid],
              ...(empByGuid[guid] ? { employeeNumbers: { [guid]: empByGuid[guid] } } : {}),
            }),
          });
          data.results.forEach((r) => { collected[r.guid] = r; });
        } catch (err) {
          collected[guid] = { guid, status: "failed", statusCode: 0, message: `${err.message} (the delete may still complete; retry to confirm)` };
        }
        if (mountedRef.current) {
          setDeleteResults((prev) => ({ ...prev, [guid]: collected[guid] }));
          setDeleteProgress({ done: Object.keys(collected).length, total: guids.length });
        }
      }
    };

    try {
      await Promise.all(Array.from({ length: DELETE_CONCURRENCY }, worker));
    } finally {
      if (mountedRef.current) {
        // Leave only failed rows selected so a retry targets them
        setSelected(new Set(Object.values(collected).filter((r) => r.status === "failed").map((r) => r.guid)));
        setPhase("done");
      }
    }
  };

  const handleExport = () => {
    const header = ["Employee Number", "IFS User GUID", "GUID Source", "Purge Reason", "Purged At", "Status", "Message"];
    const lines = rows.map((r) => {
      const res = r.guid ? deleteResults[r.guid] : null;
      const status = res ? STATUS_BADGES[res.status]?.label : r.lookup && r.lookup.status !== "found" ? LOOKUP_BADGES[r.lookup.status]?.label : "";
      const source = r.source === "datafabric" ? "Data Fabric" : "IFS lookup";
      return [r.employeeNumber, r.guid, source, r.purgeReason, r.purgeTimestamp, status, res?.message || r.lookup?.message]
        .map(csvCell)
        .join(",");
    });
    const blob = new Blob([[header.map(csvCell).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `infor_user_purge_${startDate}_to_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const results = Object.values(deleteResults);
  const summary = {
    deleted: results.filter((r) => r.status === "deleted").length,
    notFound: results.filter((r) => r.status === "not_found").length,
    failed: results.filter((r) => r.status === "failed").length,
  };
  const selectedCount = selectableRows.filter((r) => selected.has(r.guid)).length;
  const withIfsUser = rows.filter((r) => r.guid).length;

  const truncated = !!queryResult?.truncated;

  return (
    <div className="as-root">
      {/* ── Header ── */}
      <div className="as-header">
        <div className="as-header-inner">
          <div className="logo-mark">P</div>
          <div>
            <div className="as-title">Infor User Purge</div>
            <div className="as-subtitle">Remove IFS users for employees purged as NO SHOW or DUP_OR_OTHR_EE_PURGE</div>
          </div>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="as-search-bar">
        <div className="as-search-inner">
          <div className="as-search-group">
            <div className="as-search-row">
              <label className="control-label" style={{ marginBottom: 0 }}>From</label>
              <input
                type="date"
                className="cug-input as-id-input"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => { setStartDate(e.target.value); setError(""); }}
                disabled={busy}
              />
              <label className="control-label" style={{ marginBottom: 0 }}>To</label>
              <input
                type="date"
                className="cug-input as-id-input"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => { setEndDate(e.target.value); setError(""); }}
                disabled={busy}
              />
              <button className="compare-btn" onClick={runQuery} disabled={busy || !startDate || !endDate}>
                {phase === "querying" ? (
                  <>
                    <span className="spinner" /> Querying…
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    Find Purged Users
                  </>
                )}
              </button>
              <button className="cug-reset-btn" onClick={handleReset} disabled={busy}>
                Reset
              </button>

              <button className="as-export-btn" onClick={handleExport} disabled={rows.length === 0} style={{ marginLeft: "auto" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Errors ── */}
      {error && (
        <div className="as-error-bar">
          <ErrorIcon />
          {error}
        </div>
      )}
      {truncated && (
        <div className="as-error-bar">
          <ErrorIcon />
          More than {queryResult.maxRows} users matched. Narrow the date range before deleting.
        </div>
      )}

      {/* ── Info panel ── */}
      {!queryResult && phase === "idle" && !error && (
        <div className="as-info-panel">
          <div className="as-info-title">What this tool does</div>
          <div className="as-info-body">
            <p>
              Finds employees purged in myHR as NO SHOW or DUP_OR_OTHR_EE_PURGE within the selected dates (UTC, inclusive)
              and removes their user accounts from Infor IFS (KOCHIND_PRD). Dates default to the previous calendar month.
            </p>
            <div className="as-info-features">
              {[
                ["📅", "Pick any purge date range"],
                ["👀", "Review users before anything is deleted"],
                ["🗑️", "Delete selected users from IFS"],
                ["📥", "Export the list and results to CSV"],
              ].map(([icon, text]) => (
                <div key={text} className="as-info-feature">
                  <span className="as-feature-icon">{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {phase === "querying" && (
        <div className="as-empty">
          <span className="spinner" /> Running Data Fabric query{queryStatus ? ` (${queryStatus})` : ""}…
        </div>
      )}

      {/* ── Results ── */}
      {queryResult && (
        <div className="ar-results-container" style={{ margin: "24px 32px" }}>
          <div className="ar-results-header">
            <div className="ar-results-left">
              <div className="ar-results-count">
                {rows.length} {rows.length === 1 ? "user" : "users"} found · {withIfsUser} with IFS account · {selectedCount} selected
                {phase === "resolving" && (
                  <>
                    {" · "}<span className="spinner" /> Looking up {resolveProgress.done}/{resolveProgress.total} in IFS…
                  </>
                )}
                {results.length > 0 && (
                  <>
                    {" · "}<span style={{ color: "var(--green)" }}>{summary.deleted} deleted</span>
                    {" · "}<span style={{ color: "var(--amber)" }}>{summary.notFound} not found</span>
                    {" · "}<span style={{ color: "#ff6464" }}>{summary.failed} failed</span>
                  </>
                )}
              </div>
            </div>
            <div className="ar-results-actions">
              <button
                className="compare-btn sn-delete-confirm-btn"
                onClick={() => setConfirmOpen(true)}
                disabled={busy || confirmOpen || selectedCount === 0 || truncated}
              >
                {phase === "deleting" ? (
                  <>
                    <span className="spinner" /> Deleting {deleteProgress.done}/{deleteProgress.total}…
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    </svg>
                    Delete Selected ({selectedCount})
                  </>
                )}
              </button>
            </div>
          </div>

          {confirmOpen && (
            <div className="sn-delete-confirmation" style={{ marginTop: 0, marginBottom: "16px" }}>
              <div className="sn-confirmation-header">
                <h4>Confirm IFS User Deletion</h4>
              </div>
              <div className="sn-caution-message">
                <div className="sn-caution-icon">⚠️</div>
                <div className="sn-caution-text">
                  <strong>Caution:</strong> This permanently deletes {selectedCount} user account{selectedCount !== 1 ? "s" : ""} from
                  Infor IFS (KOCHIND_PRD) and cannot be undone.
                </div>
              </div>
              <div className="sn-confirmation-checkbox">
                <label>
                  <input type="checkbox" checked={confirmAck} onChange={(e) => setConfirmAck(e.target.checked)} />
                  <span>I understand I will delete {selectedCount} user(s) and this action cannot be undone</span>
                </label>
              </div>
              <div className="sn-confirmation-actions">
                <button className="compare-btn" onClick={() => { setConfirmOpen(false); setConfirmAck(false); }}>
                  Cancel
                </button>
                <button className="compare-btn sn-delete-confirm-btn" onClick={runDelete} disabled={!confirmAck}>
                  Delete Users
                </button>
              </div>
            </div>
          )}

          <div className="ar-table-container">
            <table className="ar-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <span className="sn-delete-checkbox">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={busy || truncated || selectableRows.length === 0}
                      />
                    </span>
                  </th>
                  <th>Employee</th>
                  <th>IFS User GUID</th>
                  <th>Purge Reason</th>
                  <th>Purged At</th>
                  <th>Status</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="ar-empty-state">No purged users found for this date range.</td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const res = r.guid ? deleteResults[r.guid] : null;
                    const badge = res ? STATUS_BADGES[res.status] : r.lookup && r.lookup.status !== "found" ? LOOKUP_BADGES[r.lookup.status] : null;
                    const message = res?.message || r.lookup?.message || "";
                    return (
                      <tr key={r.key}>
                        <td>
                          <span className="sn-delete-checkbox">
                            <input
                              type="checkbox"
                              checked={!!r.guid && selected.has(r.guid)}
                              onChange={() => toggleRow(r.guid)}
                              disabled={busy || truncated || !r.guid || isDone(r.guid)}
                            />
                          </span>
                        </td>
                        <td className="ar-cell-profile">{r.employeeNumber || "—"}</td>
                        <td className="ar-cell-approval">
                          {r.guid || "—"}
                          {r.source === "ifs" && r.guid && <div style={{ color: "var(--text-muted)" }}>via IFS lookup</div>}
                        </td>
                        <td><span className="ar-badge ar-state-cancelled">{r.purgeReason || "—"}</span></td>
                        <td className="ar-cell-date">{formatDate(r.purgeTimestamp)}</td>
                        <td>{badge ? <span className={badge.className}>{badge.label}</span> : "—"}</td>
                        <td className="ar-cell-comments" title={message}>{message || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="as-table-footer">
            Showing {rows.length} {rows.length === 1 ? "user" : "users"}
          </div>
        </div>
      )}
    </div>
  );
}
